import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, MoreVertical, Trash2, X, Pencil, ArrowLeft } from 'lucide-react';
import AlertsTable from './AlertsTable';
import CreateAlertModal from './modals/CreateAlertModal';
import EditAlertModal from './modals/EditAlertModal';
import FilterModal from './modals/FilterModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import StatusMessage, { StatusType } from '../../components/StatusMessage';
import { useAlerts } from '../../hooks/useAlerts';
import { AlertData } from '../../services/alertApi';
import { SimplifiedCreateAlertRequest, ApiAlertData } from '../../types/alerts';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';

const AlertsManagement: React.FC = () => {
  const navigate = useNavigate();
  const {
    alerts,
    loading,
    error,
    createSimplifiedAlert,
    updateSimplifiedAlert,
    deleteAlert,
    toggleAlertStatus,
    clearError,
  } = useAlerts();

  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    devicesForDashboard: devices,
    getDevicesForDashboard,
    demoDevice,
  } = devicesContext;

  /* User Selected Context */
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { hideBottomBar, showBottomBar, isBottomBarHidden } = userSelectedContext;

  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<ApiAlertData | null>(null);
  const [alertToDelete, setAlertToDelete] = useState<ApiAlertData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    device: '',
    status: '',
    date: '',
  });
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);

  // Status message state
  const [statusMessage, setStatusMessage] = useState<{
    type: StatusType;
    message: string;
    isVisible: boolean;
  }>({
    type: 'success',
    message: '',
    isVisible: false,
  });

  // Handle error from useAlerts hook
  useEffect(() => {
    if (error) {
      console.error('AlertsManagement: Error occurred:', error);
      showStatusMessage('error', `Error: ${error}`);
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Get user devices from API
  useEffect(() => {
    if (devices.length === 0) {
      getDevicesForDashboard();
    }
  }, []);

  // Check if mobile view
  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Un hide bottom bar when modal is closed
  useEffect(() => {
    if (screenSize <= 768 && isBottomBarHidden && !(isCreateModalOpen || isEditModalOpen)) {
      showBottomBar();
    }
  }, [isCreateModalOpen, isEditModalOpen]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showActionMenu && !(event.target as Element).closest('.mobile-action-menu') && !(event.target as Element).closest('.mobile-action-menu-button')) {
        setShowActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu]);

  // Helper function to show status messages
  const showStatusMessage = (type: StatusType, message: string) => {
    setStatusMessage({
      type,
      message,
      isVisible: true,
    });
  };

  // Close status message
  const closeStatusMessage = () => {
    setStatusMessage(prev => ({ ...prev, isVisible: false }));
  };

  // Filter alerts based on search term and filters
  const filteredAlerts = useMemo(() => {
    // Demo-alert hiding is disabled: backend deletes demo alerts once the user
    // onboards a real device, so the client no longer filters them. If that
    // behavior is reverted, uncomment the block below and restore the
    // dependency array to include demoDevice / isDemoAccountAllowed / demoDeviceId.
    // const hideDemo = !isDemoAccountAllowed();
    return (alerts || []).filter(alert => {
      // Hide alerts tied to the demo device when user has opted out
      // if (hideDemo && !Number.isNaN(demoDeviceId) && alert.deviceId === demoDeviceId) {
      //   return false;
      // }

      // Apply search filter
      if (
        searchTerm &&
        !(
          alert.alertName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          alert.deviceName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) {
        return false;
      }

      // Apply device filter
      if (filters.device !== 'All Devices' && filters.device && alert.deviceName !== filters.device) {
        return false;
      }

      // Apply status filter
      if (filters.status !== 'All Statuses' && filters.status) {
        const alertStatus = alert.isActive ? 'active' : 'inactive';
        if (alertStatus !== filters.status) {
          return false;
        }
      }

      // Apply date filter
      if (filters.date && alert.lastTriggeredAt) {
        const filterDate = new Date(filters.date);
        const alertDate = new Date(alert.lastTriggeredAt);
        if (filterDate.toDateString() !== alertDate.toDateString()) {
          return false;
        }
      }

      return true;
    });
  // demoDevice is included so the memo re-runs when the preference is toggled
  // from Settings (toggling clears/sets demoDevice via getDevicesForDashboard).
  }, [alerts, searchTerm, filters, demoDevice]);

  // Simplified alert creation using the new simplified API
  const handleCreateAlert = async (newAlert: SimplifiedCreateAlertRequest) => {
    try {
      console.log('AlertsManagement: Creating alert with data:', newAlert);
      const response = await createSimplifiedAlert(newAlert);

      if (response) {
        console.log('AlertsManagement: Alert created successfully:', response);
        setIsCreateModalOpen(false);
        showStatusMessage(
          'success',
          `Alert "${newAlert.alertName}" created successfully!`
        );
      } else {
        console.error('AlertsManagement: Failed to create alert');
        showStatusMessage('error', 'Failed to create alert. Please try again.');
      }
    } catch (error) {
      console.error('AlertsManagement: Error creating alert:', error);
      showStatusMessage('error', 'Error creating alert. Please try again.');
    }
  };

  const handleEditAlert = async (
    updatedAlert: SimplifiedCreateAlertRequest
  ) => {
    if (!selectedAlert) return;

    try {
      console.log('AlertsManagement: Updating alert with data:', updatedAlert);
      const response = await updateSimplifiedAlert(
        selectedAlert.alertId,
        updatedAlert
      );

      if (response) {
        console.log('AlertsManagement: Alert updated successfully:', response);
        setIsEditModalOpen(false);
        setSelectedAlert(null);
        showStatusMessage(
          'success',
          `Alert "${updatedAlert.alertName}" updated successfully!`
        );
      } else {
        console.error('AlertsManagement: Failed to update alert');
        showStatusMessage('error', 'Failed to update alert. Please try again.');
      }
    } catch (error) {
      console.error('AlertsManagement: Error updating alert:', error);
      showStatusMessage('error', 'Error updating alert. Please try again.');
    }
  };

  const handleDeleteClick = (alert: AlertData) => {
    console.log('handleDeleteClick called with alert:', alert);
    // Find the corresponding API alert
    const apiAlert = (alerts || []).find(a => a.alertId === alert.alertId);
    console.log('Found apiAlert for delete:', apiAlert);
    if (apiAlert) {
      setAlertToDelete(apiAlert as any);
      setIsDeleteModalOpen(true);
      console.log('Delete modal should open now');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;

    setIsDeleting(true);

    try {
      const success = await deleteAlert(alertToDelete.alertId);
      if (success) {
        console.log('AlertsManagement: Alert deleted successfully');
        showStatusMessage(
          'success',
          `Alert "${alertToDelete.alertName}" deleted successfully!`
        );
      } else {
        console.error('AlertsManagement: Failed to delete alert');
        showStatusMessage('error', 'Failed to delete alert. Please try again.');
      }
    } catch (error) {
      console.error('AlertsManagement: Failed to delete alert:', error);
      showStatusMessage('error', 'Error deleting alert. Please try again.');
    } finally {
      setIsDeleting(false);
      setAlertToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleEditClick = (alert: AlertData) => {
    console.log('handleEditClick called with alert:', alert);
    // Find the corresponding API alert
    const apiAlert = (alerts || []).find(a => a.alertId === alert.alertId);
    console.log('Found apiAlert for edit:', apiAlert);
    if (apiAlert) {
      setSelectedAlert(apiAlert as any);
      setIsEditModalOpen(true);
      console.log('Edit modal should open now');
    }
  };

  const handleStatusToggle = async (alertId: number) => {
    const result = await toggleAlertStatus(alertId);

    if (result.success) {
      console.log('AlertsManagement: Alert status updated successfully');
      if (result.isActive) {
        // Alert was activated - show green success message
        showStatusMessage('success', 'Alert made active successfully!');
      } else {
        // Alert was deactivated - show green success message
        showStatusMessage('success', 'Alert made Inactive Successfully!');
      }
    } else {
      console.error('AlertsManagement: Failed to update alert status');
      showStatusMessage(
        'error',
        'Failed to update alert status. Please try again.'
      );
    }
  };

  //handle add alert button click
  const handleAddAlertClick = () => {
    if (devices.length === 0 && !demoDevice) {
      showStatusMessage(
        'warning',
        'Please onboard a device first to create an alert. Visit the Devices page to add your first device.'
      );
      return;
    }
    setIsCreateModalOpen(true);
    if (screenSize <= 768) hideBottomBar();
  };

  // Mobile card rendering
  const renderMobileCards = () => {
    if (filteredAlerts.length === 0) {
      return (
        <div className="mobile-alerts-container">
          <div className="mobile-empty-state">
            <div className="mobile-empty-icon">📋</div>
            <h3 className="mobile-empty-title">No Alerts Found</h3>
            <p className="mobile-empty-description">
              {devices.length > 0 ? (
                searchTerm || Object.values(filters).some(f => f)
                  ? 'No alerts match your current filters. Try adjusting your search or filters.'
                  : `You haven't created any alerts yet. Create your first ${devices.length === 1 && demoDevice ? 'Demo ' : ''}alert to get started.`
              ) : (
                'You haven\'t added any devices yet. Please add a device to start creating alerts.'
              )}
            </p>
            {!searchTerm && !Object.values(filters).some(f => f) && (
              <button
                onClick={handleAddAlertClick}
                className="mobile-empty-button"
              >
                <Plus className="w-4 h-4" />
                Create First Alert
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mobile-alerts-container">
        {filteredAlerts.map((alert) => (
          <div key={alert.alertId} className="mobile-alert-card">
            <div className="mobile-alert-header">
              <div className="mobile-alert-title-section">
                <h3 className="mobile-alert-name">{alert.alertName}</h3>
                <div className="mobile-alert-status">
                  <div className={`mobile-status-dot ${alert.isActive ? 'active' : 'inactive'}`}></div>
                  <span className={`mobile-status-text ${alert.isActive ? 'active' : 'inactive'}`}>
                    {alert.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mobile-alert-actions">
                <div className="status-toggle-wrapper">
                  <button
                    className="mobile-status-toggle"
                    onClick={() => handleStatusToggle(alert.alertId)}
                  >
                    <div className={`mobile-toggle-slider ${alert.isActive ? 'active' : 'inactive'}`}>
                      <div className="mobile-toggle-handle"></div>
                    </div>
                  </button>
                  {/* <span className="status-tooltip">{alert.isActive ? 'Active' : 'Inactive'}</span> */}
                </div>
                <button
                  className="mobile-action-menu-button"
                  onClick={() => setShowActionMenu(showActionMenu === alert.alertId ? null : alert.alertId)}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="mobile-alert-content">
              {/* Device and Date Section */}
              <div className="mobile-device-date-section">
                <span
                  className="mobile-device-name mobile-device-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (alert.deviceId) {
                      navigate(`/devices/${alert.deviceId}`);
                    }
                  }}
                >
                  {alert.deviceName}
                </span>
                <span className="mobile-alert-date">
                  {alert.lastTriggeredAt ? new Date(alert.lastTriggeredAt).toLocaleString() : '--'}
                </span>
              </div>
              
              {/* Index and Sensor Readings Section */}
              <div className="mobile-readings-section">
                <div className="mobile-readings-grid">
                  {alert.alertConditions && alert.alertConditions.length > 0 ? (
                    alert.alertConditions.map((condition: any, idx: number) => {
                      const parameterName = condition.pollutantParameterName || 
                                           condition.parameterName || 
                                           `Parameter ${condition.pollutantParameterId}`;
                      const unit = condition.parameterUnitName || 
                                  condition.unitSymbol || 
                                  '';
                      return (
                        <div key={idx} className="mobile-reading-item">
                          <span className="mobile-reading-label">{parameterName}</span>
                          <span className="mobile-reading-operator">{condition.operator}</span>
                          <span className="mobile-reading-value">{condition.thresholdValue}</span>
                          {unit && <span className="mobile-reading-unit">{unit}</span>}
                        </div>
                      );
                    })
                  ) : (
                    <div className="mobile-no-conditions">No conditions</div>
                  )}
                </div>
              </div>
              
              {/* Recipients Section */}
              <div className="mobile-recipients-section">
                <span className="mobile-recipients-label">Recipients</span>
                <div className="mobile-recipients-list">
                  {alert.alertRecipients && alert.alertRecipients.length > 0 ? (
                    alert.alertRecipients.map((r: any) => r.email).join(', ')
                  ) : (
                    <span className="mobile-no-recipients">No recipients</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Mobile Action Menu (rendered at root level)
  const renderMobileActionMenu = () => {
    if (!showActionMenu) return null;
    
    const selectedAlert = filteredAlerts.find(alert => alert.alertId === showActionMenu);
    if (!selectedAlert) return null;

    return (
      <>
        <div className="mobile-action-menu-overlay" onClick={() => setShowActionMenu(null)}>
          <div className="mobile-action-menu">
            <button
              className="mobile-action-close"
              onClick={() => setShowActionMenu(null)}
            >
              <X className="w-5 h-5 text-[var(--color-text)]" />
            </button>
            <div className="mobile-action-content">
              <button
                className="mobile-action-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditClick(selectedAlert);
                  setShowActionMenu(null);
                  hideBottomBar();
                }}
              >
                <Pencil className="w-5 h-5" />
                Edit Details
              </button>
              <button
                className="mobile-action-item delete"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick(selectedAlert);
                  setShowActionMenu(null);
                }}
              >
                <Trash2 className="w-5 h-5" />
                Remove Alert
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className={`alerts-container ${screenSize <= 768 && (isCreateModalOpen || isEditModalOpen) ? 'isMobileModalOpen' : ''}`}>
        {/* Loading Indicator */}
        {loading && (
          <div className="h-[calc(100dvh-5rem)] loading-container">
            <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
            <p className="text-base text-[var(--color-sub-title-text)]">
              Loading alerts...
            </p>
          </div>
        )}

        {!loading && (
          <>
            {/* Header */}
            <div className="alerts-page-header zoomOutLevel">
              {screenSize <= 768 ? (
                <>
                  {/* Mobile Header */}
                  <div className="share-link-mobile-header">
                    <div className="share-link-mobile-header-left">
                      <ArrowLeft 
                        className="share-link-back-icon" 
                        size={24}
                        onClick={() => navigate(-1)}
                      />
                      <h1 className="share-link-mobile-title">Alerts</h1>
                    </div>
                  </div>
                </>
              ) : (
                <div className="alerts-page-header-left">
                  <h1 className="device-title">{screenSize > 860 ? 'Alerts Management' : "Alerts"}</h1>
                  <p className="alerts-subtitle device-subtitle ml-0">
                    Create, manage, and monitor your alert rules.
                  </p>
                </div>
              )}

              {/* Action Bar */}
              <div className="alerts-page-header-right">
                {/* Search Bar */}
                <div className='search-bar-container flex-1 lg:min-w-[400px]'>
                  <input
                    type="text"
                    placeholder="Search by alert name"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  <div className="search-bar-icon">
                    <Search className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Filter Button */}
                <button
                  onClick={() => setIsFilterModalOpen(true)}
                  className="alerts-filter-button"
                >
                  {screenSize > 768 && 'Filter'}{' '}
                  <Filter className="w-4 h-4 text-[var(--color-sub-title-text)]" />
                </button>

                {/* Add New Alert Button */}
                <button
                  onClick={handleAddAlertClick}
                  className="alerts-add-button"
                >
                  {screenSize > 768 ? (
                    '+ Alert'
                  ) : (
                    <Plus className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Alerts Table or Mobile Cards */}
            {screenSize > 860 ? (
              <AlertsTable
                alerts={filteredAlerts}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onStatusToggle={handleStatusToggle}
                searchTerm={searchTerm}
              />
            ) : (
              renderMobileCards()
            )}
          </>
        )}

        {isFilterModalOpen && (
          <FilterModal
            isOpen={isFilterModalOpen}
            onClose={() => setIsFilterModalOpen(false)}
            filters={filters}
            onApplyFilters={setFilters}
          />
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && alertToDelete && (
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              if (!isDeleting) {
                setIsDeleteModalOpen(false);
                setAlertToDelete(null);
              }
            }}
            onConfirm={handleDeleteConfirm}
            title="Delete Alert"
            message={`Are you sure you want to delete the alert "${alertToDelete.alertName}"? This action cannot be undone.`}
            confirmText={isDeleting ? 'Deleting...' : 'Delete'}
            cancelText="Cancel"
            type="danger"
            disabled={isDeleting}
          />
        )}

        {/* Status Message */}
        <StatusMessage
          type={statusMessage.type}
          message={statusMessage.message}
          isVisible={statusMessage.isVisible}
          onClose={closeStatusMessage}
          autoHideDelay={4000} // 4 seconds
        />

        {/* Mobile Action Menu */}
        {screenSize <= 860 && renderMobileActionMenu()}
      </div>

      {/* Create Alert Modal */}
      {isCreateModalOpen && (
        <CreateAlertModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateAlert}
        />
      )}

      {/* Edit Alert Modal */}
      {isEditModalOpen && selectedAlert && (
        <EditAlertModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdate={handleEditAlert}
          alertData={selectedAlert as ApiAlertData}
        />
      )}
    </>
  );
};

export default AlertsManagement;
